import classNames from 'classnames';
import { isNumber } from 'lodash';
import React, { useEffect } from 'react';
import {
    BaseOperateView,
    BaseStagePlugin,
    useActiveLayerData,
    useFramework,
    useIsBasicOperate,
    useIsDisableOperate,
    useIsDisablePick,
} from '@/framework';
import { getLayerAgentOperateConfig } from '@/utils/agent-teams-kit';
import { useProxyMouseEvents } from '@/utils/event-kit';
import { getLayerInfoWithRotate } from '@/utils/layer-kit';
import { resolveShortcutMenuRenderMode } from '../render-mode';
import { useShortcutMenuState } from '../states/shortcut-menu-state';
import DefaultShortcutList from './default-shortcut-list';
import layerMenuStyles from './layer-menu.module.less';
import './shortcut.less';

const ShortcutMenuView: React.FC = () => {
    const framework = useFramework();
    const isBaseOperate = useIsBasicOperate();
    const disablePick = useIsDisablePick();
    const disableOperate = useIsDisableOperate();
    const activeData = useActiveLayerData();
    const visible = useShortcutMenuState()((state) => state.visible);
    const defaultMenuEnabled = useShortcutMenuState()((state) => state.defaultMenuEnabled);
    const layerMenus = useShortcutMenuState()((state) => state.layerMenus);
    const emptySelectionRenderers = useShortcutMenuState()((state) => state.emptySelectionRenderers);

    const { mouseEvents, syntheticEvent } = useProxyMouseEvents({
        onMouseMove: () => {
            framework.service.frame.delHoverId();
        },
    });

    useEffect(() => {
        const handler = framework.service.edgelessCanvas.onPan(() => {
            syntheticEvent.emitCustomEventToDocumentWithThrottle('mousedown');
        });
        return () => {
            framework.service.events.off(handler);
        };
    }, [framework, syntheticEvent]);

    const operateLayer = activeData?.layer ? activeData.pickLayer || activeData.layer : null;
    const isLocked = operateLayer ? framework.service.flatLayer.checkIsNeedLock(operateLayer.id) : false;
    const layerMenu = operateLayer ? layerMenus[operateLayer.layerType] : undefined;
    const renderMode = resolveShortcutMenuRenderMode({
        visible,
        hasActiveLayer: !!activeData?.layer,
        blocked: isBaseOperate || disablePick || disableOperate || isLocked,
        hasLayerMenu: !!layerMenu,
        defaultMenuEnabled,
    });

    if (renderMode === 'hidden') return null;

    if (renderMode === 'empty-selection') {
        return (
            <>
                {emptySelectionRenderers.map((Renderer, index) => (
                    <Renderer key={`${Renderer.displayName || Renderer.name}-${index}`} mouseHandler={mouseEvents} />
                ))}
            </>
        );
    }

    if (!activeData?.layer || !operateLayer) return null;

    const rotate = operateLayer.rotate;
    const inTop = rotate > 170 && rotate < 190;
    const inBottom = rotate > 350 || rotate < 10;
    const convertedLayer = framework.service.flatLayer.convertLayer(operateLayer);
    if (!convertedLayer) return null;

    if (renderMode === 'default-menu') {
        let offsetTop = inTop ? 80 : 50;
        const layerOffset = framework.service.behavior.get(operateLayer)?.shortcutMenuOffsetY?.(operateLayer) || 0;
        offsetTop += layerOffset;

        return (
            <BaseStagePlugin style={{ zIndex: 19 }} name="shortcut-menu">
                <BaseOperateView layer={getLayerInfoWithRotate(convertedLayer)} offsetTop={offsetTop}>
                    <div
                        className={classNames('shortcut-operate-container', {
                            'in-top': inTop,
                            'in-bottom': inBottom,
                        })}
                        style={{ '--shortcut-menu-offset-y': `${layerOffset}px` } as React.CSSProperties}
                        {...mouseEvents}
                    >
                        <DefaultShortcutList id={activeData.layer.id} />
                    </div>
                </BaseOperateView>
            </BaseStagePlugin>
        );
    }

    if (!layerMenu) return null;

    const inRight = rotate < 280 && rotate > 260;
    const inLeft = rotate < 100 && rotate > 80;
    let offsetTop: number | undefined;
    let offsetRight: number | undefined;

    if (layerMenu.top) {
        offsetTop = layerMenu.detectionY || 50;
        if (inTop) {
            offsetTop += 30;
        }
    }

    if (layerMenu.right) {
        offsetRight = layerMenu.detectionX || 50;
        if (inRight) {
            offsetRight += 30;
        }
    }

    const layerOffset = framework.service.behavior.get(activeData.layer)?.shortcutMenuOffsetY?.(activeData.layer) || 0;
    if (isNumber(offsetTop)) {
        offsetTop += layerOffset;
    }
    if (isNumber(offsetRight)) {
        offsetRight += layerOffset;
    }

    const operateBehavior = framework.service.behavior.get(operateLayer);
    const agentOperateConfig = getLayerAgentOperateConfig(operateLayer);
    const disableRotate =
        agentOperateConfig?.disable?.rotate || operateBehavior?.disableDefaultRotateOperate?.(operateLayer);

    return (
        <BaseStagePlugin style={{ zIndex: 19 }} name="shortcut-menu">
            <BaseOperateView
                layer={getLayerInfoWithRotate(convertedLayer)}
                offsetTop={offsetTop}
                offsetRight={offsetRight}
                topLimitClassName={layerMenuStyles.shortcutTopMenuLimit}
                rightLimitClassName={layerMenuStyles.shortcutRightMenuLimit}
            >
                {layerMenu.bottom ? (
                    <div className={layerMenuStyles.shortcutBottomMenuContainer} {...mouseEvents}>
                        {layerMenu.bottom}
                    </div>
                ) : null}

                {layerMenu.top ? (
                    <div
                        className={classNames(layerMenuStyles.shortcutTopMenuContainer, {
                            [layerMenuStyles.inTop]: inTop,
                            [layerMenuStyles.inBottom]: inBottom,
                            [layerMenuStyles.disableRotate]: disableRotate,
                        })}
                        {...mouseEvents}
                    >
                        {layerMenu.top}
                    </div>
                ) : null}

                {layerMenu.innerBottom ? (
                    <div className={layerMenuStyles.shortcutInnerBottomMenuContainer} {...mouseEvents}>
                        {layerMenu.innerBottom}
                    </div>
                ) : null}

                {layerMenu.right ? (
                    <div
                        className={classNames(layerMenuStyles.shortcutRightMenuContainer, {
                            [layerMenuStyles.inRight]: inRight,
                            [layerMenuStyles.inLeft]: inLeft,
                        })}
                        {...mouseEvents}
                    >
                        {layerMenu.right}
                    </div>
                ) : null}
            </BaseOperateView>
        </BaseStagePlugin>
    );
};

export default React.memo(ShortcutMenuView);
